import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Ban,
  ScrollText,
  Landmark,
  FolderTree,
  Handshake,
  FileCheck,
  ChevronDown,
  Percent,
  Wine,
  Fuel,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { tariffCatalogService, type DetallePartida } from '../services/tariff-catalog-service';
import { pct, NOTA_SCOPE_LABELS } from './tariff-assistant-views';
import { SimuladorTributos } from './simulador-tributos';
import { GlosarioAduanero } from './glosario-aduanero';

type Pestania = 'tributos' | 'clasificacion' | 'tecnico' | 'preferencias' | 'documentos';

const PESTANIAS: Array<{ id: Pestania; label: string; icon: React.ElementType }> = [
  { id: 'tributos', label: 'Tributos', icon: Landmark },
  { id: 'clasificacion', label: 'Clasificación', icon: FolderTree },
  { id: 'tecnico', label: 'Técnico', icon: ScrollText },
  { id: 'preferencias', label: 'Preferencias', icon: Handshake },
  { id: 'documentos', label: 'Documentos', icon: FileCheck },
];

/** "NUEVOS" → "Nuevos (0 km)", "3" → "3 años". El dato viene tal cual del Apéndice I. */
function etiquetaAntiguedad(yearLabel: string): string {
  if (yearLabel === 'NUEVOS') return 'Nuevos (0 km)';
  return yearLabel === '1' ? '1 año' : `${yearLabel} años`;
}

/**
 * Los nombres de capítulo vienen EN MAYÚSCULAS del PDF. Presentación en
 * sentence case para que se puedan leer — el dato guardado queda intacto.
 */
function sentenceCase(texto: string): string {
  const bajo = texto.toLocaleLowerCase('es');
  return bajo.charAt(0).toLocaleUpperCase('es') + bajo.slice(1);
}

/**
 * Los niveles canónicos de un código boliviano de 10 dígitos, derivados por
 * truncado — la estructura del Sistema Armonizado es posicional: 4 dígitos =
 * partida, 6 = subpartida SA (mundial), 8 = NANDINA (andina), 10 = apertura
 * Bolivia. Es la misma derivación que muestra Dlogistics.
 */
function nivelesDelCodigo(
  code: string
): Array<{ etiqueta: string; digitos: number; codigo: string }> {
  const d = code.replace(/\./g, '');
  if (d.length !== 10) return [];
  return [
    { etiqueta: 'Partida', digitos: 4, codigo: `${d.slice(0, 2)}.${d.slice(2, 4)}` },
    { etiqueta: 'Subpartida SA', digitos: 6, codigo: `${d.slice(0, 4)}.${d.slice(4, 6)}` },
    {
      etiqueta: 'NANDINA',
      digitos: 8,
      codigo: `${d.slice(0, 4)}.${d.slice(4, 6)}.${d.slice(6, 8)}`,
    },
    { etiqueta: 'Código Bolivia', digitos: 10, codigo: code },
  ];
}

/**
 * Abreviaturas de la columna "Documento adicional para el despacho aduanero",
 * tal como las define el propio arancel (pág. 15 de la edición 2026).
 * Solo presentación: el valor crudo se muestra siempre al lado.
 */
const DOC_LEYENDA: Record<string, string> = {
  AP: 'Autorización Previa',
  'AP y C': 'Autorización Previa y Certificado',
  C: 'Certificado',
  'C*': 'Certificado, previa obtención del permiso sanitario o fitosanitario',
  P: 'Prohibida',
  CITES: 'Certificado de comercio internacional de especies amenazadas de fauna y flora silvestres',
  CCRT: 'Certificado de Cumplimiento de Reglamento Técnico',
  CRTM: 'Certificado de Reglamento Técnico de Muestra',
  DJ: 'Declaración Jurada',
};

/**
 * Expande un valor de la columna de documento a lenguaje llano usando la
 * leyenda oficial. Los valores compuestos ("AP - DJ", "C* o C") se expanden
 * término a término conservando el conector. Si algún término no está en la
 * leyenda se devuelve null y la UI muestra solo el valor crudo — no se adivina.
 */
function expandirTipoDocumento(tipo: string): string | null {
  if (DOC_LEYENDA[tipo]) return DOC_LEYENDA[tipo];
  const partes = tipo.split(/\s+(-|o|y)\s+/);
  if (partes.length < 3) return null;
  const expandido = partes.map((p) =>
    p === '-' ? ' + ' : p === 'o' ? ' o ' : p === 'y' ? ' y ' : DOC_LEYENDA[p]
  );
  return expandido.every((p) => p != null) ? expandido.join('') : null;
}

/**
 * Una preferencia tal como la publica el libro: "100" = liberado del GA,
 * "0" = sin rebaja, otro número = rebaja de ese % sobre el GA. Los valores no
 * numéricos (el protocolo del ACE 22) se muestran tal cual.
 */
function textoPreferencia(valor: string): string {
  const n = Number(valor);
  if (!Number.isFinite(n)) return valor;
  if (n === 100) return 'Liberado del GA (100 %)';
  if (n === 0) return 'Sin rebaja (0 %)';
  return `Rebaja del ${n} % del GA`;
}

/** Fila de la pestaña Preferencias: un acuerdo, su valor y qué significa. */
function FilaPreferencia({
  acuerdo,
  paises,
  valor,
  extra,
  traePreferencias,
}: {
  acuerdo: string;
  paises: string;
  valor: string | null | undefined;
  extra?: string | null;
  traePreferencias: boolean;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 py-2.5 border-b border-dashed last:border-b-0">
      <div>
        <p className="text-sm font-medium">{acuerdo}</p>
        <p className="text-xs text-muted-foreground">{paises}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold">
          {valor != null
            ? textoPreferencia(valor)
            : traePreferencias
              ? 'El arancel no publica rebaja por este acuerdo'
              : 'Sin dato en esta versión del catálogo'}
        </p>
        {valor != null && extra && (
          <p className="text-xs text-muted-foreground">Protocolo {extra}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Color de identidad de cada tributo. No es decoración: el mismo color viaja
 * de la tarjeta a su paso en la fórmula de liquidación y al glosario, así el
 * lector conecta "el azul de arriba es el paso 2 de abajo". Clases literales
 * porque Tailwind no compila clases armadas por template.
 */
const ACENTOS_TRIBUTO = {
  sky: {
    borde: 'border-l-sky-500',
    texto: 'text-info ',
    chip: 'bg-info/10 text-info ',
  },
  violet: {
    borde: 'border-l-violet-500',
    texto: 'text-violet-600 dark:text-violet-400',
    chip: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  },
  amber: {
    borde: 'border-l-amber-500',
    texto: 'text-alerta ',
    chip: 'bg-alerta/10 text-alerta ',
  },
  teal: {
    borde: 'border-l-teal-500',
    texto: 'text-ok ',
    chip: 'bg-ok/10 text-ok ',
  },
} as const;

export type AcentoTributo = keyof typeof ACENTOS_TRIBUTO;

/**
 * Un tributo con su explicación completa (feedback de Sergio, vault 101 §2,
 * al estilo del referente de Dlogistics): tasa grande + QUÉ es + SOBRE QUÉ
 * se paga + la norma que lo respalda. Las citas son las mismas del motor de
 * liquidación real (`customs-duty.service.ts`) — no se inventan.
 *
 * `atenuado` apaga el color del valor grande cuando el tributo no aplica
 * ("No paga" / "Sin dato"): el color fuerte queda reservado para lo que sí
 * se paga.
 */
function TributoCard({
  nombre,
  acento,
  icono: Icono,
  tasa,
  tasaDetalle,
  atenuado = false,
  queEs,
  base,
  norma,
}: {
  nombre: string;
  acento: AcentoTributo;
  icono: React.ElementType;
  tasa: string;
  tasaDetalle?: string;
  atenuado?: boolean;
  queEs: string;
  base: string;
  norma: string;
}) {
  const a = ACENTOS_TRIBUTO[acento];
  return (
    <div className={`rounded-md border border-l-4 ${a.borde} p-4 space-y-2`}>
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${a.chip}`}>
          <Icono className="h-4 w-4" />
        </span>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {nombre}
        </p>
      </div>
      <p
        className={`text-2xl font-bold tabular-nums ${atenuado ? 'text-muted-foreground' : a.texto}`}
      >
        {tasa}
        {tasaDetalle && (
          <span className="text-xs font-normal text-muted-foreground ml-2">{tasaDetalle}</span>
        )}
      </p>
      <div className="space-y-1 text-xs text-muted-foreground">
        <p>{queEs}</p>
        <p>
          <span className="font-semibold text-foreground">Se paga sobre:</span> {base}
        </p>
        <p>
          <span className="font-semibold text-foreground">Norma:</span> {norma}
        </p>
      </div>
    </div>
  );
}

/**
 * La fórmula de liquidación paso a paso, con la cita legal de cada paso.
 * Mismos pasos, mismo orden y mismas normas que `explicarLiquidacion` del
 * motor real — acá sin montos porque la ficha no conoce FOB ni flete (eso es
 * el simulador). El orden no es decorativo: el IVA se calcula sobre CIF MÁS
 * el GA — invertir los pasos da un número menor y una multa después.
 */
function FormulaLiquidacion({
  gaRate,
  isIvaExempt,
}: {
  gaRate: string | number;
  isIvaExempt: boolean;
}) {
  // El paso del GA y el del IVA llevan el color de identidad de su tarjeta —
  // es el puente visual entre "qué es cada tributo" y "dónde entra en la cuenta".
  const pasos: Array<{ formula: string; norma: string; acento?: AcentoTributo }> = [
    { formula: 'CIF = FOB + flete + seguro', norma: 'Art. 20 RLGA (D.S. 25870)' },
    { formula: `GA = CIF × ${pct(gaRate)}`, norma: 'Art. 26 LGA + arancel vigente', acento: 'sky' },
    { formula: 'Base IVA = CIF + GA (+ ICE cuando corresponde)', norma: 'Art. 20 RLGA' },
    isIvaExempt
      ? {
          formula: 'IVA = 0 — esta partida está exenta',
          norma: 'Apéndice II del arancel',
          acento: 'violet',
        }
      : {
          formula: 'IVA = Base × 14,94 % (13/87 efectivo)',
          norma: 'Art. 8 Ley 843',
          acento: 'violet',
        },
    {
      formula: 'Total tributos = GA + IVA (+ ICE / IEHD si corresponden)',
      norma: 'Art. 25 Ley 1990 (LGA)',
    },
    { formula: 'Costo nacionalizado = CIF + tributos', norma: 'Art. 90 LGA' },
  ];
  return (
    <div className="rounded-md border p-4 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Fórmula de liquidación, en el orden que manda la norma
        </p>
        <GlosarioAduanero compacto />
      </div>
      <ol className="space-y-1.5">
        {pasos.map((p, i) => (
          <li key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
            <span
              className={`inline-flex h-5 w-5 shrink-0 translate-y-0.5 items-center justify-center rounded-full text-[11px] font-bold ${
                p.acento ? ACENTOS_TRIBUTO[p.acento].chip : 'bg-muted text-muted-foreground'
              }`}
            >
              {i + 1}
            </span>
            <span className="font-mono text-[13px]">{p.formula}</span>
            <span className="text-xs text-muted-foreground">[{p.norma}]</span>
          </li>
        ))}
      </ol>
      {/*
        🔴 Acá decía "Prixia todavía no aplica ese tope". Era cierto cuando el
        simulador de esta ficha ignoraba la vía de transporte, y quedó FALSO al
        unificar la liquidación: ahora el Paso 0 aplica el 25 % y lo muestra a
        diez centímetros de esta línea. Un texto que contradice al número que
        tiene al lado hace dudar de los dos.

        Lo que sigue siendo cierto —y por eso se dice— es que la proporción no se
        contrastó contra el texto de la norma.
      */}
      <p className="text-xs text-muted-foreground border-t pt-2">
        En transporte aéreo el Art. 27 LGA limita el flete computable, y la liquidación de abajo
        aplica ese tope cuando elegís vía aérea. La proporción del 25 % viene del asistente aduanero
        de ABCD y todavía no se contrastó con el texto de la norma: cada liquidación aérea lo avisa.
      </p>
    </div>
  );
}

/**
 * El texto de una nota legal, con presentación tipográfica y NADA más: los
 * incisos a), b), c)… van con sangría real y marcador destacado, y los puntos
 * numerados con el número en negrita. El TEXTO es ley — no se toca una letra,
 * solo cómo se muestra.
 */
function TextoNotaLegal({ texto }: { texto: string }) {
  const lineas = texto.split('\n');
  // Una línea que no matchea ningún marcador pero arranca en minúscula es la
  // continuación de la anterior (el extractor del PDF parte los párrafos):
  // hereda la sangría del inciso en curso para no romper la lista.
  let enInciso = false;
  return (
    <div className="text-sm space-y-1.5">
      {lineas.map((linea, i) => {
        if (linea.trim() === '') return null;
        const inciso = linea.match(/^\s*([a-z]|[ivxl]+)\)\s+(.*)$/i);
        if (inciso) {
          enInciso = true;
          return (
            <p key={i} className="pl-5">
              <span className="font-semibold text-primary">{inciso[1]})</span> {inciso[2]}
            </p>
          );
        }
        const numerada = linea.match(/^\s*(\d+[.)])\s+(.*)$/);
        if (numerada) {
          enInciso = false;
          return (
            <p key={i}>
              <span className="font-semibold text-primary">{numerada[1]}</span> {numerada[2]}
            </p>
          );
        }
        const esContinuacion = enInciso && /^[a-záéíóúñü]/.test(linea.trim());
        if (!esContinuacion) enInciso = false;
        return (
          <p key={i} className={esContinuacion ? 'pl-5' : undefined}>
            {linea}
          </p>
        );
      })}
    </div>
  );
}

/**
 * Una nota legal como tarjeta colapsable. Las de la Sección XVI son
 * larguísimas: las largas arrancan cerradas para que la pestaña se pueda
 * recorrer; las cortas, abiertas.
 */
function NotaLegalCard({
  encabezado,
  sourcePage,
  texto,
}: {
  encabezado: string;
  sourcePage: number | null;
  texto: string;
}) {
  const [abierta, setAbierta] = useState(texto.length <= 400);
  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setAbierta((a) => !a)}
        className="flex w-full items-center justify-between gap-3 p-3 text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-primary">
          {encabezado}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {sourcePage != null && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              pág. {sourcePage}
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${abierta ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {abierta && (
        <div className="border-t px-3 py-3">
          <TextoNotaLegal texto={texto} />
        </div>
      )}
    </div>
  );
}

function AvisoAmbar({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-alerta-borde bg-alerta-suave p-3 text-sm flex gap-2">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-alerta " />
      <span>{children}</span>
    </div>
  );
}

/**
 * Ficha completa de una partida — las 5 pestañas del Asistente Arancelario.
 *
 * Regla de la ficha: cuando un dato NO está cargado se dice explícitamente
 * ("sin dato", "no cargado todavía"), nunca se muestra un guion mudo que el
 * usuario pueda leer como "no aplica". Clasificar mal por un dato callado
 * cuesta plata en la aduana.
 */
export function FichaPartida({ code, onVolver }: { code: string; onVolver: () => void }) {
  const [detalle, setDetalle] = useState<DetallePartida | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pestania, setPestania] = useState<Pestania>('tributos');

  useEffect(() => {
    let vigente = true;
    setDetalle(null);
    setError(null);
    setPestania('tributos');
    tariffCatalogService
      .detalle(code)
      .then((d) => vigente && setDetalle(d))
      .catch((e: unknown) =>
        vigente ? setError(e instanceof Error ? e.message : 'No se pudo abrir la partida') : null
      );
    return () => {
      vigente = false;
    };
  }, [code]);

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onVolver}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver a los resultados
        </Button>
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          {error}
        </div>
      </div>
    );
  }

  if (!detalle) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Nivel partida: el backend lo deriva del fullContext publicado. Fallback
  // al código pelado solo para versiones viejas sin fullContext cargado.
  const partida4 = detalle.code.replace(/\./g, '').slice(0, 4);
  const codigoPartida =
    detalle.partidaNivel?.code ?? `${partida4.slice(0, 2)}.${partida4.slice(2)}`;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onVolver}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver a los resultados
      </Button>

      {/* Encabezado */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-xl sm:text-2xl font-extrabold break-all">
            {detalle.code}
          </span>
          {detalle.importProhibited === true && <Badge variant="destructive">No importable</Badge>}
          {detalle.hasIce === true && <Badge variant="destructive">Paga ICE</Badge>}
          {detalle.isIvaExempt && <Badge variant="secondary">IVA exento</Badge>}
        </div>
        <p className="text-muted-foreground">{detalle.description}</p>
      </div>

      {/* Si no entra al país, el resto de la ficha es contexto. Va ANTES de las pestañas. */}
      {detalle.avisoProhibicion && (
        <div className="rounded-md border border-bloqueo-borde bg-bloqueo-suave p-3 text-sm flex gap-2">
          <Ban className="h-4 w-4 shrink-0 mt-0.5 text-bloqueo" />
          <div className="space-y-0.5">
            <p className="font-semibold text-bloqueo ">No se puede importar</p>
            <p>{detalle.avisoProhibicion}</p>
          </div>
        </div>
      )}

      {/* Pestañas: en pantallas chicas scrollean horizontal (patrón móvil),
          nunca se apilan en columna. */}
      <div className="flex gap-1 border-b overflow-x-auto">
        {PESTANIAS.map((p) => {
          const Icon = p.icon;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setPestania(p.id)}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                pestania === p.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {p.label}
            </button>
          );
        })}
      </div>

      {/* ── Tributos ── */}
      {pestania === 'tributos' && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <TributoCard
              nombre="Gravamen Arancelario (GA)"
              acento="sky"
              icono={Landmark}
              tasa={pct(detalle.gaRate)}
              queEs="El arancel de importación propiamente dicho: la alícuota que el arancel nacional fija para esta partida."
              base="el valor CIF — la mercadería más el flete y el seguro hasta la aduana de frontera."
              norma="Art. 26 Ley 1990 (LGA) · arancel vigente"
            />
            <TributoCard
              nombre="IVA a las importaciones"
              acento="violet"
              icono={Percent}
              atenuado={detalle.isIvaExempt}
              tasa={detalle.isIvaExempt ? 'Exento' : pct(detalle.ivaRate)}
              tasaDetalle={detalle.isIvaExempt ? undefined : 'efectivo 14,94 % (13/87)'}
              queEs={
                detalle.isIvaExempt
                  ? 'Esta partida está exenta de IVA de importación por el Apéndice II del arancel.'
                  : 'El mismo IVA del mercado interno, cobrado en la aduana al nacionalizar.'
              }
              base="CIF + GA (+ ICE cuando corresponde). Como el IVA va “por dentro”, la tasa que de verdad se aplica es 13/87 = 14,94 %, no el 13 % nominal."
              norma={
                detalle.isIvaExempt ? 'Art. 8 Ley 843 · Apéndice II del arancel' : 'Art. 8 Ley 843'
              }
            />
            <TributoCard
              nombre="Impuesto a los Consumos Específicos (ICE)"
              acento="amber"
              icono={Wine}
              atenuado={detalle.hasIce !== true}
              tasa={
                detalle.hasIce === true ? 'Paga' : detalle.hasIce === false ? 'No paga' : 'Sin dato'
              }
              queEs={
                detalle.hasIce === true
                  ? 'Grava consumos específicos: vehículos, bebidas alcohólicas, tabaco. El arancel marca que a esta partida le corresponde — la tasa va aparte.'
                  : detalle.hasIce === false
                    ? 'Grava consumos específicos (vehículos, alcohol, tabaco). Esta partida no está sujeta según el arancel vigente.'
                    : 'Grava consumos específicos (vehículos, alcohol, tabaco). El arancel no trae el dato para esta partida — verificá antes de liquidar.'
              }
              base="para vehículos, la tasa depende de la antigüedad (Apéndice I del arancel — si está cargada, la tabla aparece abajo); para alcohol y tabaco la fija la Ley 843, fuera del arancel."
              norma="Ley 843 · Apéndice I del arancel"
            />
            <TributoCard
              nombre="Impuesto Especial a los Hidrocarburos y sus Derivados (IEHD)"
              acento="teal"
              icono={Fuel}
              atenuado={detalle.hasIehd !== true}
              tasa={
                detalle.hasIehd === true
                  ? 'Paga'
                  : detalle.hasIehd === false
                    ? 'No aplica'
                    : 'Sin dato'
              }
              queEs={
                detalle.hasIehd === true
                  ? 'Grava hidrocarburos y sus derivados. El arancel marca que a esta partida le corresponde.'
                  : detalle.hasIehd === false
                    ? 'Grava hidrocarburos y sus derivados. Esta partida no está sujeta según el arancel vigente.'
                    : 'Grava hidrocarburos y sus derivados. El arancel no trae el dato para esta partida — verificá antes de liquidar.'
              }
              base="se liquida por litro o por unidad de medida, no como porcentaje del CIF. La tasa vive en la norma, fuera del arancel, y no está cargada en Prixia."
              norma="Ley 1053"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Unidad física de medida para la declaración:{' '}
            {detalle.unitOfMeasure ? (
              <span className="font-mono font-semibold text-foreground">
                {detalle.unitOfMeasure}
              </span>
            ) : (
              'sin dato en esta versión del catálogo'
            )}
          </p>

          <FormulaLiquidacion gaRate={detalle.gaRate} isIvaExempt={detalle.isIvaExempt} />

          {/*
            `ICE_NO_INCLUIDO` se omite acá porque la pestaña ya lo dice arriba,
            en la tarjeta del ICE y en su propio aviso ámbar — con más contexto
            del que cabe en una línea al pie de la tabla.
          */}
          <SimuladorTributos
            code={detalle.code}
            omitirAvisos={
              detalle.hasIce === true && detalle.iceRates.length === 0 ? ['ICE_NO_INCLUIDO'] : []
            }
          />

          {detalle.iceRates.length > 0 && (
            <div className="rounded-md border p-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Tasa de ICE por antigüedad (Apéndice I del arancel)
              </p>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Antigüedad del vehículo</TableHead>
                      <TableHead className="text-right">Tasa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detalle.iceRates.map((t) => (
                      <TableRow key={t.yearLabel}>
                        <TableCell className="text-sm">{etiquetaAntiguedad(t.yearLabel)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">
                          {t.importProhibited ? (
                            <Badge variant="destructive">No importable a esa antigüedad</Badge>
                          ) : (
                            pct(t.rate)
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/*
            🔴 Decía "su tasa por antigüedad todavía no está cargada". La
            antigüedad solo aplica a vehículos: en una cerveza de malta esa
            frase manda a buscar un dato que para esa partida no existe.

            El texto ahora dice lo que el arancel realmente sabe —QUÉ paga ICE,
            no CUÁNTO— y deja la antigüedad como el caso particular que es.
          */}
          {detalle.hasIce === true && detalle.iceRates.length === 0 && (
            <AvisoAmbar>
              Esta partida paga ICE y su tasa no está cargada en Prixia: el arancel indica que
              corresponde, no cuánto. Para vehículos depende de la antigüedad (Apéndice I del
              arancel); para bebidas alcohólicas y tabaco la fija la Ley 843, fuera del arancel.
              <strong> Las liquidaciones de abajo no lo incluyen</strong> — verificalo a mano antes
              de comprometer un precio.
            </AvisoAmbar>
          )}

          {detalle.avisoIce && detalle.hasIce !== true && (
            <AvisoAmbar>{detalle.avisoIce}</AvisoAmbar>
          )}
        </div>
      )}

      {/* ── Clasificación ── */}
      {pestania === 'clasificacion' && (
        <div className="space-y-4">
          {nivelesDelCodigo(detalle.code).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {nivelesDelCodigo(detalle.code).map((n) => (
                <div
                  key={n.digitos}
                  className="flex items-center gap-2 rounded-md border px-2.5 py-1.5"
                >
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                    {n.etiqueta} · {n.digitos}
                  </Badge>
                  <span className="font-mono text-sm font-semibold">{n.codigo}</span>
                </div>
              ))}
            </div>
          )}
          <div className="rounded-md border p-4">
            <ul className="divide-y divide-dashed">
              <li className="flex flex-wrap items-baseline gap-3 py-2.5">
                <span className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                  Sección
                </span>
                <span className="font-mono text-sm font-bold w-28 shrink-0">
                  {detalle.section ?? '—'}
                </span>
                <span className="text-sm">
                  {detalle.sectionTitle
                    ? sentenceCase(detalle.sectionTitle)
                    : 'Título de la sección no cargado en esta versión'}
                </span>
              </li>
              <li className="flex flex-wrap items-baseline gap-3 py-2.5">
                <span className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                  Capítulo
                </span>
                <span className="font-mono text-sm font-bold w-28 shrink-0">{detalle.chapter}</span>
                <span className="text-sm">
                  {detalle.chapterName ? sentenceCase(detalle.chapterName) : '—'}
                </span>
              </li>
              <li className="flex flex-wrap items-baseline gap-3 py-2.5">
                <span className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                  Partida
                </span>
                <span className="font-mono text-sm font-bold w-28 shrink-0">{codigoPartida}</span>
                <span className="text-sm">
                  {detalle.partidaNivel?.text ?? (
                    <span className="text-muted-foreground">
                      Texto de la partida no cargado en esta versión del catálogo
                    </span>
                  )}
                </span>
              </li>
              {/* Ramas intermedias tal como las publica el arancel. Algunas no
                  tienen código propio (agrupadores sin numerar) — no se les
                  inventa uno. */}
              {(detalle.ramasIntermedias ?? []).map((rama, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-3 py-2.5">
                  <span className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                    {i === 0 ? 'Subdivisión' : ''}
                  </span>
                  <span className="w-28 shrink-0" />
                  <span className="text-sm text-muted-foreground">{rama.replace(/:$/, '')}</span>
                </li>
              ))}
              <li className="flex flex-wrap items-baseline gap-3 py-2.5">
                <span className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                  Código Bolivia
                </span>
                <span className="font-mono text-sm font-bold w-28 shrink-0">{detalle.code}</span>
                <span className="text-sm font-medium">
                  {detalle.description}
                  {detalle.unitOfMeasure ? (
                    <span className="text-muted-foreground font-normal">
                      {' '}
                      · unidad: {detalle.unitOfMeasure}
                    </span>
                  ) : null}
                </span>
              </li>
            </ul>
          </div>
          <p className="text-xs text-muted-foreground">
            Cada nivel viene del arancel publicado — es la cadena que hace legible una descripción
            que sola diría “{detalle.description}”.
          </p>
        </div>
      )}

      {/* ── Técnico ── */}
      {pestania === 'tecnico' && (
        <div className="space-y-4">
          {detalle.notas.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Notas legales que alcanzan a esta partida
              </p>
              {detalle.notas.map((n, i) => (
                <NotaLegalCard
                  key={i}
                  encabezado={
                    `${NOTA_SCOPE_LABELS[n.scope] ?? n.scope} ` +
                    (n.scope === 'SECCION' ? (detalle.section ?? '') : detalle.chapter) +
                    (n.heading ? ` · ${n.heading.replace(/\.$/, '')}` : '')
                  }
                  sourcePage={n.sourcePage}
                  texto={n.text}
                />
              ))}
              <p className="text-xs text-muted-foreground pt-1">
                Las notas son parte del texto legal del arancel: definen qué entra y qué no en cada
                capítulo, y mandan por encima de la descripción de la partida.
              </p>
            </div>
          ) : (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No hay notas legales cargadas para la sección ni el capítulo de esta partida.
            </div>
          )}

          {(detalle.aperturas.length > 0 || detalle.sinonimos.length > 0) && (
            <div className="rounded-md border p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Lo que tu empresa le colgó a esta partida
              </p>
              {detalle.aperturas.length > 0 && (
                <ul className="text-sm space-y-1">
                  {detalle.aperturas.map((a) => (
                    <li key={a.id}>
                      <span className="font-mono text-xs font-semibold">{a.internalCode}</span> —{' '}
                      {a.description}
                    </li>
                  ))}
                </ul>
              )}
              {detalle.sinonimos.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {detalle.sinonimos.map((s) => (
                    <Badge key={s.id} variant="secondary">
                      {s.term}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Preferencias ── */}
      {pestania === 'preferencias' && (
        <div className="space-y-4">
          <div className="rounded-md border p-4 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Preferencias arancelarias por acuerdo — columnas del arancel oficial
            </p>
            <FilaPreferencia
              acuerdo="CAN · ACE 36 · ACE 47"
              paises="Comunidad Andina (Perú, Colombia, Ecuador) · Mercosur · Cuba"
              valor={detalle.prefCanAce36Ace47}
              traePreferencias={detalle.traePreferencias}
            />
            <FilaPreferencia
              acuerdo="ACE 22 — Chile"
              paises="Acuerdo de Complementación Económica con Chile"
              valor={detalle.prefAce22Chi}
              extra={detalle.prefAce22Prot}
              traePreferencias={detalle.traePreferencias}
            />
            <FilaPreferencia
              acuerdo="ACE 66 — México"
              paises="Acuerdo de Complementación Económica con México"
              valor={detalle.prefAce66Mex}
              traePreferencias={detalle.traePreferencias}
            />
            <p className="text-xs text-muted-foreground pt-2">
              La rebaja aplica <strong>solo al gravamen arancelario (GA)</strong> y exige
              certificado de origen válido del país exportador — el IVA y el ICE se pagan igual.
            </p>
          </div>
          {!detalle.traePreferencias && (
            <AvisoAmbar>
              Esta versión del catálogo no trae las columnas de preferencias, así que “sin dato” no
              significa que no exista rebaja: verificá el acuerdo aplicable antes de cotizar.
            </AvisoAmbar>
          )}
        </div>
      )}

      {/* ── Documentos ── */}
      {pestania === 'documentos' && (
        <div className="space-y-4">
          {detalle.importProhibited === true ? (
            <div className="rounded-md border border-bloqueo-borde bg-bloqueo-suave p-3 text-sm flex gap-2">
              <Ban className="h-4 w-4 shrink-0 mt-0.5 text-bloqueo" />
              <span>
                No hay documento que habilite esta importación: la mercadería está prohibida. No
                tramites nada — no entra al país.
              </span>
            </div>
          ) : detalle.avisoRegimenVehiculos ? (
            <AvisoAmbar>
              <strong>Régimen de importación de vehículos:</strong> {detalle.avisoRegimenVehiculos}
            </AvisoAmbar>
          ) : detalle.dispatchDocType && detalle.avisoDocumento ? (
            <>
              <div className="rounded-md border p-4 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Documento adicional para el despacho aduanero
                </p>
                <div className="flex flex-wrap items-baseline gap-3 py-2 border-b border-dashed">
                  <span className="w-32 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                    Documento
                  </span>
                  <span className="text-sm">
                    <span className="font-mono font-bold">{detalle.dispatchDocType}</span>
                    {expandirTipoDocumento(detalle.dispatchDocType) && (
                      <> — {expandirTipoDocumento(detalle.dispatchDocType)}</>
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap items-baseline gap-3 py-2 border-b border-dashed">
                  <span className="w-32 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                    Entidad que lo emite
                  </span>
                  <span className="text-sm">
                    {detalle.dispatchDocEntity ?? (
                      <span className="text-muted-foreground">El arancel no la especifica</span>
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap items-baseline gap-3 py-2">
                  <span className="w-32 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                    Norma que lo exige
                  </span>
                  <span className="text-sm">
                    {detalle.dispatchDocLegalBasis ?? (
                      <span className="text-muted-foreground">El arancel no la especifica</span>
                    )}
                  </span>
                </div>
              </div>
              <AvisoAmbar>
                Sin ese documento la mercadería no se despacha, aunque los impuestos estén pagados.
                Tramitalo antes de embarcar.
              </AvisoAmbar>
            </>
          ) : (
            <div className="rounded-md border p-4 text-sm">
              El arancel no exige un permiso previo de despacho para esta partida.
            </div>
          )}
          {detalle.borderDispatch && detalle.importProhibited !== true && (
            <div className="rounded-md border p-4 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Despacho en frontera
              </p>
              <p className="text-sm">
                El arancel marca esta partida para despacho en frontera:{' '}
                <span className="font-mono font-semibold">{detalle.borderDispatch}</span>. El
                trámite aduanero se hace en la aduana de frontera, no en una aduana interior —
                planificá la ruta y el despachante con eso en mente.
              </p>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Esto cubre solo lo que exige el arancel. Otros requisitos (registro sanitario,
            etiquetado, normas técnicas) pueden venir de normas fuera del arancel y no están
            cargados en Prixia.
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground border-t pt-3">
        {detalle.version.nombre}
        {detalle.version.norma ? ` · ${detalle.version.norma}` : ''}
      </p>
    </div>
  );
}
